import { Component } from '@angular/core';
import { StatsBenefitsComponent } from "../stats-benefits/stats-benefits.component";
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService, UserInfo } from 'app/services/api.service'
import { StatData } from 'app/interfaces/stat.interface';
import { Router } from '@angular/router';
import { LinkComponent } from "../link/link.component";
import { LoyaltyByCountry } from 'app/interfaces/loyalty-by-country.interface';
import { environment } from '../../../environments/environment';
import { CommonModule } from '@angular/common';
import { MenuService } from 'app/services/menu.service';
import { MenuItemType } from 'app/interfaces/api/MenuItemType';

@Component({
    selector: 'app-header-benefits',
    standalone: true,
    imports: [CommonModule, StatsBenefitsComponent, TranslateModule, LinkComponent, LinkComponent],
    templateUrl: './header-benefits.component.html',
    styleUrl: './header-benefits.component.scss',
})
export class HeaderBenefitsComponent {
    userInfo: UserInfo
    statsData: StatData[]
    nextLevel: LoyaltyByCountry | null = null
    public isLoyalty2026Active: boolean = environment.loyalty2026
    currentSales: number = 0
    currentExpositions: number = 0
    completedDisplays: number = 0
    availableActios: number = 0
    progressText: string = ''
    public canExchangePoints: boolean = false

    constructor(private api: ApiService, private router: Router, private translate: TranslateService, private menuService: MenuService
    ) {}

    private translations(){
        this.translate.get([
            'benefits_kb.loyalty_2026.become_elite_exclusive_benefits',
            'benefits_kb.loyalty_2026.elite_level_up_to_premium',
            'benefits_kb.loyalty_2026.maintain_elite_stay_elite',
            'benefits_kb.loyalty_2026.premium_enjoy_benefits',
            'benefits_kb.loyalty_2026.maintain_premium_keep_benefits'
        ])
    }

    private appendDisplaysText(message: string, displaysNeeded: number): string {
        if (displaysNeeded <= 0) return message
        
        const word = displaysNeeded === 1 
            ? this.translate.instant('benefits_kb.loyalty_2026.exposition_singular')
            : this.translate.instant('benefits_kb.loyalty_2026.exposition_plural')
        
        const suffix = this.translate.instant('benefits_kb.loyalty_2026.and_x_expositions', { 
            count: displaysNeeded, 
            word: word 
        })
        
        return message + suffix
    }

    ngOnInit(): void {
        this.api.userInfo.subscribe((userInfo) => {
            this.userInfo = userInfo
            setTimeout(() => this.updateProgressText(), 0)
            if (userInfo.loyaltyLevel && userInfo.country)
                this.api.getNextLoyaltyLevelRequirements(userInfo.loyaltyLevel, userInfo.country).subscribe((requirements) => {
                    this.nextLevel = requirements
                })
        })
        
        // Recuperar datos desde localStorage
        const userInfo = localStorage.getItem('weUserInfo')
        if (userInfo) {
            const parsed = JSON.parse(userInfo)
            const { ytdSales, completedDisplays, Completed_Displays__c, availableActios, authorities } = parsed
            this.currentSales = ytdSales || 0
            this.completedDisplays = (completedDisplays ?? Completed_Displays__c ?? 0) || 0
            this.currentExpositions = this.completedDisplays
            this.availableActios = availableActios || 0
            this.canExchangePoints = Array.isArray(authorities) && authorities.some((a: any) => a === 'canExchangePoints')
            setTimeout(() => this.updateProgressText(), 0)
        }
        
        // Actualizar cuando cambie el idioma
        this.translate.onLangChange.subscribe(() => {
            this.updateProgressText()
        })
    }
    
    updateProgressText(): void {
        this.progressText = this.getProgressText()
    }

    getLoyaltyProgress(): number {
        // Progreso ponderado: ventas y displays tienen el mismo peso (50% cada uno)
        // Elite: 12 ventas + 2 displays | Premium: 24 ventas + 3 displays
        const sales = this.currentSales || 0
        const displays = this.currentExpositions || 0

        const clamp = (v: number, max: number) => Math.max(0, Math.min(v, max))

        // Determinar si alcanzó Elite para calcular progreso hacia Premium
        const reachedElite = sales >= 12 && displays >= 2
        
        // Objetivos de Elite
        const eliteSales = 12
        const eliteDisplays = 2
        
        // Objetivos de Premium
        const premiumSales = 24
        const premiumDisplays = 3

        if (!reachedElite) {
            // Progreso hacia Elite (0-50% de la barra total)
            const salesProgress = (clamp(sales, eliteSales) / eliteSales) * 50 // 50% del peso
            const displaysProgress = (clamp(displays, eliteDisplays) / eliteDisplays) * 50 // 50% del peso
            const eliteProgress = (salesProgress + displaysProgress) / 100 // Normalizar a 0-1
            return eliteProgress * 50 // Elite representa el 50% de la barra total (0-50%)
        } else {
            // Progreso hacia Premium (50-100% de la barra total)
            const salesProgress = (clamp(sales, premiumSales) / premiumSales) * 50
            const displaysProgress = (clamp(displays, premiumDisplays) / premiumDisplays) * 50
            const premiumProgress = (salesProgress + displaysProgress) / 100
            return 50 + (premiumProgress * 50) // 50% base de Elite + progreso a Premium (0-50%)
        }
    }
    
    getNextYearLevel(): string {
        if (this.currentSales >= 24 && this.currentExpositions >= 3) {
            return 'premium'
        } else if (this.currentSales >= 12 && this.currentExpositions >= 2) {
            return 'elite'
        } else {
            return 'advanced'
        }
    }
    
    getProgressText(): string {
        if (!this.userInfo?.loyaltyLevel) return ''
        
        const currentLevel = this.userInfo.loyaltyLevel.toLowerCase()
        const sales = this.currentSales || 0
        const displays = this.currentExpositions || 0
        const eliteSales = 12, eliteDisplays = 2
        const premiumSales = 24, premiumDisplays = 3

        // Usuario Premium
        if (currentLevel === 'premium') {
            const hasReachedPremiumGoals = sales >= premiumSales && displays >= premiumDisplays
            
            if (hasReachedPremiumGoals) {
                // PREMIUM CON OBJETIVOS CUMPLIDOS
                return this.translate.instant('benefits_kb.loyalty_2026.keep_registering_update_displays')
            } else {
                // PREMIUM SIN OBJETIVOS CUMPLIDOS
                const salesNeeded = Math.max(premiumSales - sales, 0)
                const displaysNeeded = Math.max(premiumDisplays - displays, 0)
                const salesWord = salesNeeded === 1 ? 
                    this.translate.instant('benefits_kb.loyalty_2026.sale_singular') : 
                    this.translate.instant('benefits_kb.loyalty_2026.sale_plural')
                const displaysWord = displaysNeeded === 1 ? 
                    this.translate.instant('benefits_kb.loyalty_2026.exposition_singular') : 
                    this.translate.instant('benefits_kb.loyalty_2026.exposition_plural')
                return this.translate.instant('benefits_kb.loyalty_2026.maintain_level_premium_dynamic', { 
                    sales: salesNeeded,
                    salesWord: salesWord,
                    displays: displaysNeeded,
                    displaysWord: displaysWord
                })
            }
        }

        // Usuario Elite
        if (currentLevel === 'elite') {
            const hasReachedEliteGoals = sales >= eliteSales && displays >= eliteDisplays
            
            if (hasReachedEliteGoals) {
                // ELITE CON OBJETIVOS CUMPLIDOS - nivel up hacia Premium
                const salesNeeded = Math.max(premiumSales - sales, 0)
                const displaysNeeded = Math.max(premiumDisplays - displays, 0)
                const salesWord = salesNeeded === 1 ? 
                    this.translate.instant('benefits_kb.loyalty_2026.sale_singular') : 
                    this.translate.instant('benefits_kb.loyalty_2026.sale_plural')
                const displaysWord = displaysNeeded === 1 ? 
                    this.translate.instant('benefits_kb.loyalty_2026.exposition_singular') : 
                    this.translate.instant('benefits_kb.loyalty_2026.exposition_plural')
                return this.translate.instant('benefits_kb.loyalty_2026.level_up_premium_requirements_dynamic', { 
                    sales: salesNeeded,
                    salesWord: salesWord,
                    displays: displaysNeeded,
                    displaysWord: displaysWord
                })
            } else {
                // ELITE SIN OBJETIVOS CUMPLIDOS - mantener nivel
                const salesNeeded = Math.max(eliteSales - sales, 0)
                const displaysNeeded = Math.max(eliteDisplays - displays, 0)
                const salesWord = salesNeeded === 1 ? 
                    this.translate.instant('benefits_kb.loyalty_2026.sale_singular') : 
                    this.translate.instant('benefits_kb.loyalty_2026.sale_plural')
                const displaysWord = displaysNeeded === 1 ? 
                    this.translate.instant('benefits_kb.loyalty_2026.exposition_singular') : 
                    this.translate.instant('benefits_kb.loyalty_2026.exposition_plural')
                return this.translate.instant('benefits_kb.loyalty_2026.maintain_level_requirements_elite_dynamic', { 
                    sales: salesNeeded,
                    salesWord: salesWord,
                    displays: displaysNeeded,
                    displaysWord: displaysWord
                })
            }
        }

        // Usuario Advanced
        if (displays === 0) {
            // ADVANCED SIN PUNTOS (sin displays)
            return this.translate.instant('benefits_kb.loyalty_2026.level_up_requirements')
        } else {
            // ADVANCED (con al menos un display)
            const salesNeeded = Math.max(eliteSales - sales, 0)
            const displaysNeeded = Math.max(eliteDisplays - displays, 0)
            const salesWord = salesNeeded === 1 ? 
                this.translate.instant('benefits_kb.loyalty_2026.sale_singular') : 
                this.translate.instant('benefits_kb.loyalty_2026.sale_plural')
            const displaysWord = displaysNeeded === 1 ? 
                this.translate.instant('benefits_kb.loyalty_2026.exposition_singular') : 
                this.translate.instant('benefits_kb.loyalty_2026.exposition_plural')
            return this.translate.instant('benefits_kb.loyalty_2026.level_up_one_display_dynamic', { 
                sales: salesNeeded,
                salesWord: salesWord,
                displays: displaysNeeded,
                displaysWord: displaysWord
            })
        }
    }
    
    hasAchievedObjectives(): boolean {
        if (!this.userInfo?.loyaltyLevel) return false
        const currentLevel = this.userInfo.loyaltyLevel.toLowerCase()
        if (currentLevel === 'premium') {
            return this.currentSales >= 24 && this.currentExpositions >= 3
        }
        return false
    }
    
    getSalesObjective(): number {
        if (!this.userInfo?.loyaltyLevel) return 12
        const currentLevel = this.userInfo.loyaltyLevel.toLowerCase()
        const sales = this.currentSales || 0
        const displays = this.currentExpositions || 0
        
        // Si es Premium, siempre mostrar objetivo Premium
        if (currentLevel === 'premium') return 24
        
        // Si es Elite
        if (currentLevel === 'elite') {
            // Si ya alcanzó los objetivos de Elite, mostrar objetivo Premium
            const hasReachedEliteGoals = sales >= 12 && displays >= 2
            return hasReachedEliteGoals ? 24 : 12
        }
        
        // Advanced u otros: mostrar objetivo Elite
        return 12
    }
    
    getDisplaysObjective(): number {
        if (!this.userInfo?.loyaltyLevel) return 2
        const currentLevel = this.userInfo.loyaltyLevel.toLowerCase()
        const sales = this.currentSales || 0
        const displays = this.currentExpositions || 0
        
        // Si es Premium, siempre mostrar objetivo Premium
        if (currentLevel === 'premium') return 3
        
        // Si es Elite
        if (currentLevel === 'elite') {
            // Si ya alcanzó los objetivos de Elite, mostrar objetivo Premium
            const hasReachedEliteGoals = sales >= 12 && displays >= 2
            return hasReachedEliteGoals ? 3 : 2
        }
        
        // Advanced u otros: mostrar objetivo Elite
        return 2
    }
    
    getCurrentYearDeadline(): string {
        const currentYear = new Date().getFullYear()
        const deadlineDate = new Date(currentYear, 11, 31)
        const currentLang = this.translate.currentLang || 'es'
        const localeMap: { [key: string]: string } = {
            'es': 'es-ES', 'en': 'en-US', 'fr': 'fr-FR',
            'de': 'de-DE', 'it': 'it-IT', 'pt': 'pt-BR'
        }
        const locale = localeMap[currentLang] || 'es-ES'
        return deadlineDate.toLocaleDateString(locale, {
            day: '2-digit', month: '2-digit', year: 'numeric'
        })
    }
    
    getTitleText(): string {
        if (!this.userInfo?.loyaltyLevel) return ''
        const currentLevel = this.userInfo.loyaltyLevel.toLowerCase()
        const objectivesAchieved = this.hasAchievedObjectives()
        
        const highlightLevels = (text: string): string => {
            if (!text) return ''
            return text.replace(/(ELITE|PREMIUM)/g, '<span class="level">$1<\/span>')
        }

        let key = ''
        if (currentLevel === 'advanced') {
            key = 'benefits_kb.loyalty_2026.become_elite_exclusive_benefits'
        } else if (currentLevel === 'elite') {
            key = objectivesAchieved
                ? 'benefits_kb.loyalty_2026.elite_level_up_to_premium'
                : 'benefits_kb.loyalty_2026.maintain_elite_stay_elite'
        } else if (currentLevel === 'premium') {
            key = objectivesAchieved
                ? 'benefits_kb.loyalty_2026.premium_enjoy_benefits'
                : 'benefits_kb.loyalty_2026.maintain_premium_keep_benefits'
        }

        const translated = key ? this.translate.instant(key) : ''
        return highlightLevels(translated)
    }

    goToRegisterSales() {
        this.router.navigate(['/my-sales'])
    }

    goToDisplays() {
        this.router.navigate(['/my-displays'])
    }

    exchangePoints() {
                if (!this.canExchangePoints) return
                this.api.getRewardsProviderUrl().subscribe({
                    next: (next) => {
                        this.menuService.goTo({ type: MenuItemType.EXTERNAL_LINK, code: 'xoxoday', link: next.redirectUrl, requiresContract: 'retail-rewards' })
                        // window.open(next.redirectUrl, '_blank')
                    },
                })
    }

    discoverMore() {
        this.router.navigate(['/retail-rewards'])
    }

}
